import {
    ContainerEvent,
    HorizontalNoteContainer
} from "../model/containers";
import {
    Note,
    NoteDuration,
    TimeSignature
} from "../model/core";
import {
    GridResolution,
    gridResolutionToTicks
} from "../model/grid";
import { inputPlaneStore } from "../stores/InspectorPanelStore";
import { workspaceStore } from "../stores/WorkspaceStore";
import { NoteSelectionGroup } from "../utils/NoteSelectionGroup";
import { RydKeybindingTypes } from "./common";


export namespace PianoRollEditKB {

    export type CurrentSelectionContext = {
        trackId: number,
        container: HorizontalNoteContainer,
        notes: ContainerEvent<Note>[]
    };

    export type TimeSignatureContext = {
        timeSignature: TimeSignature
    };

    export type KeybindingHandlers = {
        octaveUp: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext[]>,
        octaveDown: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext[]>,
        duplicateNotes: RydKeybindingTypes.ContextualHandler<{ c: CurrentSelectionContext[], ts: TimeSignatureContext }>,
        fillNotes: RydKeybindingTypes.ContextualHandler<{ c: CurrentSelectionContext[], ts: TimeSignatureContext }>,
        splitNotes: RydKeybindingTypes.ContextualHandler<{ c: CurrentSelectionContext[], ts: TimeSignatureContext }>,
        deleteNotes: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext[]>,
        nudgeNotesUp: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext[]>,
        nudgeNotesDown: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext[]>,
        nudgeNotesLeft: RydKeybindingTypes.ContextualHandler<{ c: CurrentSelectionContext[], ts: TimeSignatureContext }>,
        nudgeNotesRight: RydKeybindingTypes.ContextualHandler<{ c: CurrentSelectionContext[], ts: TimeSignatureContext }>,
        selectAllNotes: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext>,
        copyNotes: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext>,
        cutNotes: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext>,
        pasteNotes: RydKeybindingTypes.ContextualHandler<CurrentSelectionContext>,
        // undo: RydKeybindingTypes.Handler,
        // redo: RydKeybindingTypes.Handler
        currentNoteSelectionDataResolver: () => CurrentSelectionContext[],
        currentTimeSignatureResolver: () => TimeSignatureContext
    };
    export function create(): KeybindingHandlers {
        return {
            octaveUp: octaveUp,
            octaveDown: octaveDown,
            duplicateNotes: duplicateNotes,
            fillNotes: fillNotes,
            splitNotes: splitNotes,
            deleteNotes: deleteNotes,
            nudgeNotesUp: nudgeNotesUp,
            nudgeNotesDown: nudgeNotesDown,
            nudgeNotesLeft: nudgeNotesLeft,
            nudgeNotesRight: nudgeNotesRight,
            selectAllNotes: selectAllNotes,
            copyNotes: ({ container, notes }) => { },
            cutNotes: ({ container, notes }) => { },
            pasteNotes: ({ container, notes }) => { },
            // undo: ({container, notes}) => { },
            // redo: ({container, notes}) => { }
            currentNoteSelectionDataResolver: currentNoteSelectionDataResolver,
            currentTimeSignatureResolver: currentTimeSignatureResolver
        };
    }

    function updateContainer(
        container: HorizontalNoteContainer,
        oldNotes: ContainerEvent<Note>[],
        newNotes: ContainerEvent<Note>[]
    ) {
        oldNotes.forEach(n => {
            container.remove(n.step, n);
        });
        newNotes.forEach(n => {
            container.put(n.step, {
                step: n.step,
                payload: n.payload,
                duration: n.duration,
                velocity: n.velocity
            });
        });
        workspaceStore.getState().setLines([...workspaceStore.getState().lines]);
    }

    type GeometryType = ReturnType<NoteSelectionGroup['getGeometry']>;

    function transformGroup(
        k: number,
        newNotes: ContainerEvent<Note>[],
        geometryTransform: (_: GeometryType) => GeometryType
    ): NoteSelectionGroup | undefined {
        const old = inputPlaneStore.getState().noteSelectionGroups?.get(k);
        if (!old) return undefined;
        const nsg = new NoteSelectionGroup(
            old.getContainer(),
            geometryTransform(old.getGeometry()),
            newNotes
        );
        return nsg;
    }

    function updateNoteSelectionGroup(
        k: number,
        newNotes: ContainerEvent<Note>[],
        geometryTransform: (_: GeometryType) => GeometryType = (g) => g
    ) {
        const nsg = transformGroup(k, newNotes, geometryTransform);
        if (!nsg) return;
        const newMap = new Map(inputPlaneStore.getState().noteSelectionGroups);
        newMap.set(k, nsg);
        inputPlaneStore.getState().setNoteSelectionGroups(newMap);
    }

    function updateNoteSelectionGroups(
        newNotes: Map<number, ContainerEvent<Note>[]>,
        geometryTransform: (_: GeometryType) => GeometryType = (g) => g
    ) {
        const newMap = new Map<number, NoteSelectionGroup>();
        const keys = inputPlaneStore.getState().noteSelectionGroups?.keys();
        if (!keys) return;
        Array.from(keys).forEach(k => {
            const nsg = transformGroup(k, newNotes.get(k) || [], geometryTransform);
            if (!nsg) return;
            newMap.set(k, nsg);
        });

        inputPlaneStore.getState().setNoteSelectionGroups(newMap);
    }

    function getGridResolution(): GridResolution {
        return workspaceStore.getState().uiState.gridResolution;
    }

    function currentNoteSelectionDataResolver(): CurrentSelectionContext[] {
        const r: CurrentSelectionContext[] = []
        inputPlaneStore.getState().noteSelectionGroups.forEach((nsg, k) => {
            if (!nsg) return;
            r.push({
                trackId: k,
                container: nsg.getContainer(),
                notes: nsg.getNotes()
            });
        });
        return r;
    }
    function currentTimeSignatureResolver(): TimeSignatureContext {
        return {
            timeSignature: workspaceStore.getState().computedContextFrame.timeSignature
        };
    }
    function octaveUp(c: CurrentSelectionContext[]) {
        c.forEach(p => {
            const newNotes = p.notes.map(n => {
                return { ...n, payload: n.payload.up(12) };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        top: g.top + 12,
                    };
                });
        });
    }

    function octaveDown(c: CurrentSelectionContext[]) {
        c.forEach(p => {
            const newNotes = p.notes.map(n => {
                return { ...n, payload: n.payload.down(12) };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        top: g.top - 12,
                    };
                });
        });
    }

    function duplicateNotes(ctx: { c: CurrentSelectionContext[], ts: TimeSignatureContext }) {
        console.log('duplicateNotes', ctx);
        ctx.c.forEach(p => {
            // create new notes at the next bar
            const delta = gridResolutionToTicks('bar' as GridResolution, p.container.getPPQ(), ctx.ts.timeSignature);
            const newNotes = p.notes.map(n => {
                return { ...n, step: n.step + delta };
            });
            updateContainer(p.container, [], newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        width: g.width + delta,
                    }
                });
        });
    }

    function collectsNoteByStart(container: HorizontalNoteContainer): Map<number, number[]> {
        const notesByStart = new Map();
        container.getGrid().forEach((n, i) => {
            const step = i;
            n.forEach(note => {
                if (!notesByStart.has(note.payload.index)) {
                    notesByStart.set(note.payload.index, []);
                }
                notesByStart.get(note.payload.index)?.push(step);
            });
        });

        return notesByStart;
    }

    function fillNotes(ctx: { c: CurrentSelectionContext[], ts: TimeSignatureContext }) {
        ctx.c.forEach(p => {
            /* stretches the note durations up to the end of the current bar or the next
            note starting (at any pitch) within this container, whichever comes first.
            */
            if (p.notes.length === 0) return;
            const ppq = p.container.getPPQ();
            const barLength = gridResolutionToTicks('bar' as GridResolution, p.container.getPPQ(), ctx.ts.timeSignature);
            const containerEnd = p.container.getLength();
            // const notesByStart = [...p.container.getAllEvents()].sort((a, b) => a.step - b.step);
            const newNotes: ContainerEvent<Note>[] = [];

            const notesByStart: Map<number, number[]> = collectsNoteByStart(p.container);
            p.notes.forEach(n => {
                const noteEnd = n.step + n.duration.value;
                // find the next note starting after this note
                const nextNote = notesByStart.get(n.payload.index)?.find((other, i) => i > n.step);
                let newDuration = 0;
                if (nextNote) {
                    newDuration = nextNote - n.step;
                } else {
                    // no next note, stretch to the end of the bar or container end
                    const currentBarEnd = Math.ceil(noteEnd / barLength) * barLength;
                    newDuration = Math.min(currentBarEnd, containerEnd) - n.step;
                }
                if (newDuration <= 0) {
                    newDuration = n.duration.value;
                }
                newNotes.push({ ...n, duration: new NoteDuration(newDuration) });
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        width: Math.max(...newNotes.map(n => n.step + n.duration.value)) - g.left,
                    };
                });
        });
    }

    function splitNotes(ctx: { c: CurrentSelectionContext[], ts: TimeSignatureContext }) {
        ctx.c.forEach(p => {
            /* splits each note into various notes with duration equal to the current grid
            resolution, starting at the original note start time, and continuing until the
            original note end time.
            */
            if (p.notes.length === 0) return;
            const gridResolution = getGridResolution();
            const ppq = p.container.getPPQ();

            const newNoteDuration = gridResolutionToTicks(gridResolution, ppq, ctx.ts.timeSignature);
            const newNotes: ContainerEvent<Note>[] = [];
            p.notes.forEach(n => {
                const noteEnd = n.step + n.duration.value;
                let currentStep = n.step;
                while (currentStep < noteEnd) {
                    const stepDuration = Math.min(newNoteDuration, noteEnd - currentStep);
                    newNotes.push({
                        step: currentStep,
                        payload: n.payload,
                        duration: new NoteDuration(newNoteDuration),
                        velocity: n.velocity
                    });
                    currentStep += stepDuration;
                }
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => g
            );
        });
    }

    function deleteNotes(p: CurrentSelectionContext[]) {
        p.forEach(p => {
            updateContainer(p.container, p.notes, []);
        });
    }

    function nudgeNotesUp(c: CurrentSelectionContext[]) {
        c.forEach(p => {
            const newNotes = p.notes.map(n => {
                return { ...n, payload: n.payload.up(1) };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        top: g.top + 1,
                    };
                });
        });
    }

    function nudgeNotesDown(c: CurrentSelectionContext[]) {
        c.forEach(p => {
            const newNotes = p.notes.map(n => {
                return { ...n, payload: n.payload.down(1) };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        top: g.top - 1,
                    };
                });
        });
    }

    function nudgeNotesLeft(ctx: { c: CurrentSelectionContext[], ts: TimeSignatureContext }) {
        ctx.c.forEach(p => {
            // moves notes left by the current grid resolution, but not before the start of the container (step 0).
            const gridResolution = getGridResolution();
            const delta = gridResolutionToTicks(gridResolution, p.container.getPPQ(), ctx.ts.timeSignature);
            const newNotes = p.notes.map(n => {
                return { ...n, step: Math.max(0, n.step - delta) };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        left: Math.max(0, g.left - delta),
                    };
                });
        });
    }
    function nudgeNotesRight(ctx: { c: CurrentSelectionContext[], ts: TimeSignatureContext }) {
        ctx.c.forEach(p => {
            // moves notes right by the current grid resolution.
            const gridResolution = getGridResolution();
            const delta = gridResolutionToTicks(gridResolution, p.container.getPPQ(), ctx.ts.timeSignature);
            const newNotes = p.notes.map(n => {
                return { ...n, step: n.step + delta };
            });
            updateContainer(p.container, p.notes, newNotes);
            updateNoteSelectionGroup(
                p.trackId,
                newNotes,
                (g) => {
                    return {
                        ...g,
                        left: Math.max(0, g.left + delta),
                    };
                });
        });
    }

    function selectAllNotes(p: CurrentSelectionContext) {
        // selects all notes in the container
    }
}
